import React from "react";
import { useSelector } from "react-redux";
import { selectUser } from "../../../store/slices/authentication";
import {
  useGetAdminStatusQuery,
  useGetAdminNotificationsUnreadCountQuery,
} from "../../../store/slices/api";
import { SharedHorizontalTabs } from "../../shared";
import Overview from "./Overview";
import BulletinManagement from "./BulletinManagement";
import MarketplaceManagement from "./MarketplaceManagement";
import UserManagement from "./UserManagement";
import CorrespondenceManagement from "./CorrespondenceManagement";
import Notifications from "./Notifications";

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "bulletinPosts", label: "Bulletin Posts" },
  { key: "marketplaceItems", label: "Marketplace Items" },
  { key: "correspondence", label: "Correspondence" },
  { key: "users", label: "Users" },
  { key: "notifications", label: "Notifications" },
];

function Admin() {
  const user = useSelector(selectUser);
  const individualId = user?.externalId ? parseInt(user.externalId) : null;

  const { data: adminData, isLoading } = useGetAdminStatusQuery(individualId, {
    skip: !individualId,
  });
  const isAdmin = adminData?.is_admin || false;

  // Unread badge for the Notifications tab (only fetched for admins).
  const { data: unreadData } = useGetAdminNotificationsUnreadCountQuery(undefined, {
    skip: !isAdmin,
  });
  const unreadCount = unreadData?.unread_count || 0;

  const { currentTabIndex, setSearchParams } =
    SharedHorizontalTabs.useGetTabIndex(TABS);

  const handleTabChange = (index) => {
    setSearchParams({ tab: TABS[index].key }, { replace: true });
  };

  if (isLoading) {
    return null;
  }

  // Double-check: even if someone types /admin in the browser, block non-admins
  if (!isAdmin) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-dupontCream dark:bg-darkPrimary">
        <div className="text-[24px] font-display font-bold text-dupontBlue dark:text-darkText mb-[8px]">
          Access Denied
        </div>
        <div className="text-[16px] text-dupontGray dark:text-darkTextSecondary">
          You do not have admin privileges.
        </div>
      </div>
    );
  }

  const renderTabContent = () => {
    switch (currentTabIndex) {
      case 1:
        return <BulletinManagement individualId={individualId} />;
      case 2:
        return <MarketplaceManagement individualId={individualId} />;
      case 3:
        return <CorrespondenceManagement individualId={individualId} />;
      case 4:
        return <UserManagement individualId={individualId} />;
      case 5:
        return <Notifications />;
      case 0:
      default:
        return <Overview individualId={individualId} />;
    }
  };

  return (
    <div className="min-h-screen flex flex-col px-4 md:px-[60px] py-[30px] gap-y-[40px] bg-dupontCream dark:bg-darkPrimary transition-colors duration-200">
      <div className="flex flex-col items-center">
        <div className="flex flex-col items-center text-center">
          <h1 className="text-[42px] font-display font-bold text-dupontBlue dark:text-darkText mb-[12px]">
            Admin Dashboard
          </h1>
          <div className="w-[80px] h-[1px] bg-dupontGold"></div>
        </div>
      </div>
      <div className="flex flex-col gap-y-[30px]">
        <SharedHorizontalTabs.Component
          tabs={TABS.map((tab) =>
            tab.key === "notifications" && unreadCount > 0
              ? `${tab.label} (${unreadCount})`
              : tab.label
          )}
          initialIndex={currentTabIndex}
          onChange={handleTabChange}
        />
        {renderTabContent()}
      </div>
    </div>
  );
}

export default Admin;
